import { useCallback, useEffect, useState } from "react";
import { callAPI } from "../../utils/api";
import { WootingCustomerName } from "../../utils/constants/types";
import useWootingUser from "../../utils/hooks/useWootingUser";
import { useSuccessToast, useErrorToast } from "../../utils/showToast";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import {
  DISCARD_TEXT,
  EDIT_NAME_ARIA_LABEL,
  FIRST_NAME_LABEL,
  LAST_NAME_LABEL,
  SAVE_TEXT,
} from "../../utils/constants/i18n-strings";
import {
  Button,
  IconButton,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalTrigger,
  VStack,
} from "@wootingkb/component-library";
import { BsFillPencilFill } from "react-icons/bs";

const NAME_SAVED_TOAST = msg({
  id: "User_name_saved_toast",
  message: "Name saved!",
});

interface editNameModalProps {
  userName: WootingCustomerName;
}

export default function EditNameModal({ userName }: editNameModalProps) {
  const [wootingUser, mutateUser] = useWootingUser();
  const [customerName, setCustomerName] =
    useState<WootingCustomerName>(userName);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const showErrorToast = useErrorToast();
  const showSuccessToast = useSuccessToast();
  const { _: i18n } = useLingui();

  const reset = useCallback(() => {
    setCustomerName(userName);
    setIsOpen(false);
  }, [userName]);

  useEffect(() => {
    setCustomerName(userName);
  }, [userName]);

  async function updateName() {
    const { first_name, last_name } = customerName;
    if (first_name.length === 0 || last_name.length === 0) return;

    try {
      setIsLoading(true);
      await callAPI(["user/name"], {
        method: "PUT",
        args: { first_name, last_name },
      });
      // wootingUser should always be defined at this point, but we need to make sure we have the
      // correct reference to trigger mutating the user namne
      if (wootingUser) {
        await mutateUser({
          ...wootingUser,
          customer_name: { first_name, last_name },
        });
      }
      showSuccessToast(i18n(NAME_SAVED_TOAST));
      reset();
    } catch (error) {
      showErrorToast(error);
    }
    setIsLoading(false);
  }

  return (
    <Modal open={isOpen} onOpenChange={setIsOpen}>
      <ModalTrigger asChild>
        <IconButton
          size="sm"
          variant="muted"
          className="absolute top-0 right-0"
          aria-label={i18n(EDIT_NAME_ARIA_LABEL)}
        >
          <BsFillPencilFill />
        </IconButton>
      </ModalTrigger>
      <ModalContent className="max-h-full overflow-y-auto md:max-h-[90vh]">
        <ModalBody
          title={
            <Trans id="User_edit_name_modal_title">Change your user name</Trans>
          }
        >
          <VStack className="gap-1">
            <Label>{i18n(FIRST_NAME_LABEL)}</Label>
            <Input
              type="text"
              className="rb-mask"
              value={customerName.first_name}
              onChange={(e) =>
                setCustomerName({ ...customerName, first_name: e.target.value })
              }
            />
          </VStack>
          <VStack className="gap-1">
            <Label>{i18n(LAST_NAME_LABEL)}</Label>
            <Input
              type="text"
              className="rb-mask"
              value={customerName.last_name}
              onChange={(e) =>
                setCustomerName({ ...customerName, last_name: e.target.value })
              }
            />
          </VStack>
        </ModalBody>
        <ModalFooter className="flex justify-end">
          <Button variant="text" size="sm" onClick={reset}>
            {i18n(DISCARD_TEXT)}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={updateName}
            isLoading={isLoading}
          >
            {i18n(SAVE_TEXT)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
